/**
 * GameHelper - Utilities for Unity game integration
 * Handles game URLs and parameters for the Unity WebGL game
 *
 * NOTE: Game files are NOT in git repository
 * - In production: Deployed to Firebase Hosting
 * - Locally: Need to bundle from Firebase Storage (game stored as ZIP files)
 * - The game is a Unity WebGL build that creates window.unityInstance
 *
 * GAME DEPLOYMENT PATHS:
 * - Firebase Hosting: served under `/game/*` and `/game-staging/*`
 * - Game ZIPs in Cloud Storage:
 *   - PROD (intraction-f177a): `gs://intraction-f177a.appspot.com/game-releases/`
 *   - DEV  (intraction-dev):  `gs://intraction-dev.firebasestorage.app/game-releases-staging/`
 * - CI bundles (downloads + extracts) the latest ZIP into `transition-web-evolve/public/` before deploy
 * - Local (after bundle): public/game/index.html
 * - URLs:
 *   - PROD: https://intraction-web.web.app/game/index.html
 *   - DEV:  https://intraction-dev.web.app/game/index.html
 */

import config from '../Config'
import { sanitizeSprintActivityGameOptions } from '../../../pure-js/libs/SprintActivityGameOptions'
import { isAgentModeEnabled } from '../agentTesting/agentMode'

export const GAME_NAME = 'Intraction Game'
// Uses /game/index.html (same path as old, but different Firebase project)
const GAME_URL = '/game/index.html' // Served from intraction-web Firebase project
const GAME_DEVELOP_URL = '/game-staging/index.html' // Develop version

/** Game launch params. Game receives only customSlotLength (slot in seconds); display buffer is not passed. */
export type GameParams = {
  sessionId: string
  displayName: string
  introduction?: boolean
  teamDisplayName?: string
  teamId?: string
  sprintId?: string
  numberOfLoops?: number
  customSlotLength?: number
  minTimeLeftToAllowRestart?: number
  finalReflectionTimeOut?: number
}

/**
 * Adds query parameters to a URL.
 *
 * @param {string} url - Base URL.
 * @param {T} queryParams - Query parameters to append.
 * @return {string} URL with serialized query params.
 */
function addQueryParamsToUrl<T extends object>(url: string, queryParams: T): string {
  const searchParams = new URLSearchParams()

  for (const [key, value] of Object.entries(queryParams)) {
    if (value === undefined || value === null || value === '') {
      continue
    }
    searchParams.set(key, String(value))
  }

  const queryString = searchParams.toString()
  if (!queryString) return url

  return `${url}?${queryString}`
}

/**
 * Gets the appropriate game URL with query parameters.
 * Game receives only customSlotLength (slot in seconds); display buffer is not passed.
 *
 * @param {GameParams} p - Game launch parameters.
 * @return {string} Game URL.
 */
export const getGameUrl = (p: GameParams): string => {
  const { numberOfLoops, customSlotLength, minTimeLeftToAllowRestart, finalReflectionTimeOut, ...rest } = p
  const path = config.enableDevelop ? GAME_DEVELOP_URL : GAME_URL
  const base = config.gameBaseUrl ?? ''
  const url = base ? `${base}${path}` : path
  const gameOptions = sanitizeSprintActivityGameOptions({
    numberOfLoops,
    customSlotLength,
    minTimeLeftToAllowRestart,
    finalReflectionTimeOut
  })
  return addQueryParamsToUrl(url, {
    ...rest,
    ...gameOptions,
    // the Unity build reads these under its own StartupProperties names — send both spellings so
    // every game build receives the configured values instead of falling back to its defaults
    ...(gameOptions.numberOfLoops !== undefined ? { fixedNumberOfLoops: gameOptions.numberOfLoops } : {}),
    ...(gameOptions.minTimeLeftToAllowRestart !== undefined
      ? { minimumSecondsLeftToTriggerRestart: gameOptions.minTimeLeftToAllowRestart }
      : {}),
    // AI-agentic-testing: tells the game to boot its AgentBridge (dev + agent mode only).
    ...(isAgentModeEnabled() ? { agentControl: 'true' } : {})
  })
}
